/**
 * Reports Page for Admin PWA - Enhanced with real data
 */

import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Users,
  Package,
  Download,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { useBranch } from '../context/BranchContext';
import { SimpleBarChart, SimpleDonutChart } from '../components/Charts';
import { accountHeaders } from '@/lib/pwaAccount';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ReportsPage() {
  const [period, setPeriod] = useState('week');
  const [salesData, setSalesData] = useState(null);
  const [topItems, setTopItems] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getBranchHeaders } = useBranch();

  const getHeaders = () => ({
    ...accountHeaders(),
    ...getBranchHeaders()
  });


  const fetchAllData = useLatestCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchSalesData(),
      fetchTopItems(),
      fetchOrderTypes(),
      fetchHourlySales()
    ]);
    setLoading(false);
  });

  // Loads on mount and when the period changes (the branch header is read at call time).
  useEffect(() => {
    fetchAllData();
  }, [period, fetchAllData]);

  const fetchSalesData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/dashboard-stats`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setSalesData(data);
      }
    } catch (err) {
      console.error('Failed to fetch sales data:', err);
    }
  };

  const fetchTopItems = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/top-items?period=${period}&limit=10`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setTopItems(data);
      }
    } catch (err) {
      console.error('Failed to fetch top items:', err);
    }
  };

  const fetchOrderTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/order-types?period=${period}`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        const colors = {
          pickup: '#8b5cf6',
          delivery: '#3b82f6',
          dine_in: '#f97316',
          unknown: '#64748b'
        };
        setOrderTypes(data.map(d => ({
          label: d.type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()),
          value: d.count,
          total: d.total,
          color: colors[d.type] || colors.unknown
        })));
      }
    } catch (err) {
      console.error('Failed to fetch order types:', err);
    }
  };

  const fetchHourlySales = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/sales-by-hour`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        // Show business hours (8am - 10pm)
        setHourlyData(data.slice(8, 22).map(d => ({
          label: `${d.hour}`,
          value: d.revenue
        })));
      }
    } catch (err) {
      console.error('Failed to fetch hourly sales:', err);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value === 'string') return value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Reports & Analytics</h1>
          <p className="text-slate-400 text-sm sm:text-base mt-1">Analyze your business performance</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList className="bg-slate-800 h-9">
              <TabsTrigger value="day" className="data-[state=active]:bg-emerald-600 text-xs sm:text-sm px-2 sm:px-3">Today</TabsTrigger>
              <TabsTrigger value="week" className="data-[state=active]:bg-emerald-600 text-xs sm:text-sm px-2 sm:px-3">Week</TabsTrigger>
              <TabsTrigger value="month" className="data-[state=active]:bg-emerald-600 text-xs sm:text-sm px-2 sm:px-3">Month</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 h-9">
            <Download className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-slate-400 text-xs sm:text-sm">Total Revenue</p>
                <p className="text-lg sm:text-2xl font-bold text-white mt-0.5 sm:mt-1 truncate">
                  {salesData?.weekSales || '$0'}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-emerald-500/10 rounded-lg ml-2">
                <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-1.5 sm:mt-2 text-xs sm:text-sm">
              <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
              <span className="text-emerald-500">{salesData?.revenueChange || '+0%'}</span>
              <span className="text-slate-500 hidden sm:inline">vs last period</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-slate-400 text-xs sm:text-sm">Total Orders</p>
                <p className="text-lg sm:text-2xl font-bold text-white mt-0.5 sm:mt-1">
                  {salesData?.weekOrders || '0'}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-blue-500/10 rounded-lg ml-2">
                <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-1.5 sm:mt-2 text-xs sm:text-sm">
              <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
              <span className="text-emerald-500">{salesData?.ordersChange || '+0%'}</span>
              <span className="text-slate-500 hidden sm:inline">vs last period</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-slate-400 text-xs sm:text-sm">Avg Order Value</p>
                <p className="text-lg sm:text-2xl font-bold text-white mt-0.5 sm:mt-1 truncate">
                  {salesData?.avgOrderValue || '$0'}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-purple-500/10 rounded-lg ml-2">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-1.5 sm:mt-2 text-xs sm:text-sm">
              <span className="text-slate-500 truncate">{salesData?.aovChange || '+0%'} <span className="hidden sm:inline">vs last period</span></span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-slate-400 text-xs sm:text-sm">New Customers</p>
                <p className="text-lg sm:text-2xl font-bold text-white mt-0.5 sm:mt-1">
                  {salesData?.weekNewCustomers || '0'}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-amber-500/10 rounded-lg ml-2">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-1.5 sm:mt-2 text-xs sm:text-sm">
              <span className="text-slate-500">This {period}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales by Hour */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg">Sales by Hour</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[200px] flex items-center justify-center">
                <div className="animate-pulse text-slate-500">Loading...</div>
              </div>
            ) : hourlyData.length > 0 ? (
              <SimpleBarChart data={hourlyData} height={200} barColor="#10b981" />
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-500">
                No sales data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Types */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg">Orders by Type</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[200px] flex items-center justify-center">
                <div className="animate-pulse text-slate-500">Loading...</div>
              </div>
            ) : orderTypes.length > 0 ? (
              <div className="flex items-center justify-center py-4">
                <SimpleDonutChart data={orderTypes} size={160} thickness={30} />
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-500">
                No order data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Items Table */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-lg flex items-center justify-between">
            <span>Top Selling Items</span>
            <Badge variant="outline" className="text-slate-400 border-slate-700">
              {period === 'day' ? 'Today' : period === 'week' ? 'This Week' : 'This Month'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="h-12 bg-slate-800 rounded animate-pulse" />
              ))}
            </div>
          ) : topItems.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">#</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Item</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Qty Sold</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {topItems.map((item, i) => (
                    <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                      <td className="py-3 px-4 text-slate-500">{i + 1}</td>
                      <td className="py-3 px-4 text-white font-medium">{item.name}</td>
                      <td className="py-3 px-4 text-right text-slate-300">{item.quantity}</td>
                      <td className="py-3 px-4 text-right text-emerald-400 font-medium">{item.revenue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              No items sold in this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue by Order Type */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-2 px-3 sm:px-6">
          <CardTitle className="text-white text-base sm:text-lg">Revenue by Order Type</CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          {orderTypes.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {orderTypes.map((type, i) => (
                <div 
                  key={i} 
                  className="p-3 sm:p-4 rounded-lg border border-slate-800"
                  style={{ borderLeftColor: type.color, borderLeftWidth: 4 }}
                >
                  <p className="text-slate-400 text-xs sm:text-sm">{type.label}</p>
                  <p className="text-lg sm:text-xl font-bold text-white mt-1">{type.total}</p>
                  <p className="text-slate-500 text-xs sm:text-sm">{type.value} orders</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 text-sm">
              No revenue data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
